import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Gallery.css';
import Footer from '../Components/Footer';

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const SERVER_URL = 'https://api.avtechbmt.cloud';

  /* ---------------------------------------------------------
     Robust image URL builder
     --------------------------------------------------------- */
  const getImageUrl = useCallback((path) => {
    if (!path || typeof path !== 'string') return '';

    const trimmed = path.trim();

    // Already absolute
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    // Protocol-relative
    if (/^\/\//.test(trimmed)) return `https:${trimmed}`;

    // Normalize backslashes + strip leading slashes
    const cleanPath = trimmed.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${SERVER_URL}/${cleanPath}`;
  }, []);

  const handleImageError = useCallback((id, fullUrl) => {
    console.error('[Gallery] Image failed to load:', fullUrl);
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  }, []);

  /* ---------------------------------------------------------
     Fetch ALL active gallery items
     ---------------------------------------------------------
     - `?limit=100&isActive=true` prevents the backend from
       silently capping the result to a small default.
     - Client-side filter guarantees only active items render
       even if the backend forgets to filter.
     - Sorted by `order` asc, then `createdAt` desc.
     --------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const fetchGallery = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${SERVER_URL}/api/v1/gallery?limit=100&isActive=true`
        );

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Server returned ${response.status} (non-JSON response)`);
        }

        const result = await response.json();
        if (cancelled) return;

        if (!response.ok) {
          throw new Error(result.message || `Failed to load gallery (${response.status})`);
        }

        const rawList = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
          ? result.data
          : [];

        // Keep only active items (defensive — server should already filter)
        const activeList = rawList.filter((item) => {
          if (item.isActive === undefined) return true; // trust server if flag missing
          if (typeof item.isActive === 'boolean') return item.isActive;
          if (typeof item.isActive === 'string') return item.isActive.toLowerCase() === 'true';
          return Boolean(item.isActive);
        });

        // Sort by order asc, then createdAt desc
        activeList.sort((a, b) => {
          const oa = Number(a.order) || 0;
          const ob = Number(b.order) || 0;
          if (oa !== ob) return oa - ob;
          const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return cb - ca;
        });

        console.log(
          '[Gallery] API returned', rawList.length,
          'item(s); active after filter:', activeList.length
        );

        setGalleryItems(activeList);

        if (activeList.length === 0) {
          setError('No public gallery items available.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load gallery photos', err);
        setError(err.message || 'Failed to load gallery');
        setGalleryItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchGallery();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Esc closes modal */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    if (selectedImage) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedImage]);

  /* ---------------------------------------------------------
     Derived stats
     --------------------------------------------------------- */
  const totalItems = galleryItems.length;

  const totalCategories = useMemo(() => {
    const set = new Set(
      galleryItems.map((item) => item.category).filter(Boolean)
    );
    return set.size;
  }, [galleryItems]);

  return (
    <div className="full-gallery-page">
      {/* ============ HERO HEADER ============ */}
      <header className="gallery-hero-header">
        <Link to="/" className="back-link">
          ← Back to Home
        </Link>

        <div className="gallery-hero-badge">
          <span className="gallery-hero-badge-dot" />
          <span className="gallery-tag">Archive · 04</span>
        </div>

        <h1>
          Life &amp; <em>Workspace.</em>
        </h1>

        <p>
          Exploring moments, campus highlights, and engineering cohorts
          — a visual archive from the AV Tech studio.
        </p>

        {/* Hero meta row */}
        <div className="gallery-hero-meta">
          <div className="gallery-hero-meta-item">
            <span className="gallery-meta-label">Photos</span>
            <span className="gallery-meta-value">
              {loading ? '—' : `${totalItems}`}
            </span>
          </div>
          <div className="gallery-hero-meta-item">
            <span className="gallery-meta-label">Collections</span>
            <span className="gallery-meta-value">
              {loading ? '—' : totalCategories}
            </span>
          </div>
          <div className="gallery-hero-meta-item">
            <span className="gallery-meta-label">Status</span>
            <span className="gallery-meta-value gallery-meta-value--live">
              <span className="gallery-live-dot" /> Updated
            </span>
          </div>
          <div className="gallery-hero-meta-item">
            <span className="gallery-meta-label">Location</span>
            <span className="gallery-meta-value">Baramati, IN</span>
          </div>
        </div>
      </header>

      {/* ============ GALLERY GRID ============ */}
      <main className="gallery-container">
        <div className="gallery-section-head">
          <h2 className="gallery-section-title">The Full Archive</h2>
          <span className="gallery-section-num">
            01 / {loading ? '—' : `${totalItems}`} Photos
          </span>
        </div>

        {loading ? (
          <div className="gallery-skeleton">Loading photos…</div>
        ) : galleryItems.length === 0 ? (
          <div className="gallery-empty">
            {error || 'No photos in the archive yet.'}
          </div>
        ) : (
          <div className="gallery-showcase-grid">
            {galleryItems.map((item, idx) => {
              const isOdd = idx % 2 !== 0;
              const fullImageUrl = getImageUrl(item.imageUrl);
              const displayIndex = idx + 1 < 10 ? `0${idx + 1}` : idx + 1;
              const isFailed = !!imageErrors[item._id];

              return (
                <div
                  key={item._id || idx}
                  className={`gallery-card-item ${isOdd ? 'odd-item' : 'even-item'} ${isFailed ? 'failed-image' : ''}`}
                  onClick={() => !isFailed && setSelectedImage(item)}
                >
                  <div className="gallery-img-wrapper">
                    {!isFailed ? (
                      <img
                        src={fullImageUrl}
                        alt={item.title || 'Gallery Item'}
                        loading="lazy"
                        onError={() => handleImageError(item._id, fullImageUrl)}
                      />
                    ) : (
                      <div className="gallery-img-placeholder">
                        <span>Image Unavailable</span>
                      </div>
                    )}
                    {!isFailed && (
                      <div className="gallery-hover-overlay">
                        <span className="view-btn">View Photo ↗</span>
                      </div>
                    )}
                  </div>

                  <div className="gallery-item-meta">
                    <span className="gallery-index">{displayIndex}</span>
                    <div className="gallery-title-box">
                      <h4>{item.title}</h4>
                      <span className="gallery-category-badge">
                        {item.category}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ============ LIGHTBOX MODAL ============ */}
      {selectedImage && (
        <div
          className="gallery-modal-backdrop"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="gallery-modal-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="gallery-modal-img-container">
              <img
                src={getImageUrl(selectedImage.imageUrl)}
                alt={selectedImage.title}
                onError={(e) => {
                  e.currentTarget.style.opacity = '0.3';
                  e.currentTarget.alt = 'Image failed to load';
                }}
              />
            </div>

            <div className="gallery-modal-details">
              <span className="gallery-tag">// {selectedImage.category}</span>
              <h3>{selectedImage.title}</h3>
              {selectedImage.description && (
                <p>{selectedImage.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}